import { fetchHistory } from '@/api/lendersApi'
import useDebounce from '@/hooks/useDebounce';
import { useSortableData } from '@/hooks/useSortableData';
import TableLoader from '@/loaders/TableLoader';
import { numberWithCommas } from '@/utils/helpers';
import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import styled from 'styled-components';
import { ReactComponent as NextIcon } from '@/assets/icons/nav-right.icon.svg';
import { ReactComponent as PrevIcon } from '@/assets/icons/nav-left.icon.svg';
import { useParams, useSearchParams } from 'react-router-dom';
import TableRowsSelect from '@/components/TableRowSelect';
import { formatInTimeZone } from 'date-fns-tz';

export const LendersHistory = () => {
  const { id } = useParams();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const limit = parseInt(searchParams.get('limit')) || 10;
  const PAGE_LIMIT = 10;
  const debouncedsearch = useDebounce(search, 500);

  const history = useQuery({
    queryKey: ['history', { id, page, limit, search: debouncedsearch }],
    queryFn: fetchHistory
  })

  const { items, requestSort, sortConfig } = useSortableData(history?.data?.data?.histories || []);
  const getClassNamesFor = (name) => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === name ? sortConfig.direction : undefined;
  }

  const handleRowsPerPageChange = (value) => {
    setSearchParams({ tab: 'history', page, limit: value });
  };

  //Paginate
  const fetchNextPage = () => {
    const totalPage = Math.ceil(history.data?.data?.meta?.totalItems / limit);
    // const totalPage = Math.ceil(history.data?.data?.meta?.totalItems / PAGE_LIMIT);
    if (page < totalPage) {
      setPage(page + 1)
    }
  }

  const fetchPrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  }

  if (history.isFetching) {
    return <TableLoader />
  }

  return (
    <LenderHistoryView>
      <TableHead>
        <div className='type'>
          <span>Customer</span>
        </div>
        <div className='provider'>
          <span>Type</span>
        </div>
        <div className='amount'>
          <span>Facility</span>
        </div>
        <div className='amount'>
          <span>Revenue</span>
        </div>
        <div className='amount'>
          <span>Take</span>
        </div>
        <div className='amount'>
          <span>Repayment</span>
        </div>
        <div className='created'>
          <span>Date closed</span>
        </div>
      </TableHead>
      <TableBody>
        {items.map((item) => {
          return (
            <div key={item?.id} className="item">
              <div className="type">
                <span>{item?.customer || '-'}</span>
              </div>
              <div className="provider">
                <span>
                  {item?.type
                    .toLowerCase()
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (char) => char.toUpperCase()) || '-'}
                </span>
              </div>
              <div className="amount">
                <span>₦{numberWithCommas(item?.facility || '00.00')}</span>
              </div>
              <div className="amount">
                <span>₦{numberWithCommas(item?.revenue || '00.00')}</span>
              </div>
              <div className="amount">
                <span>₦{numberWithCommas(item?.take || '00.00')}</span>
              </div>
              <div className="amount">
                <span>₦{numberWithCommas(item?.repayment || '00.00')}</span>
              </div>
              <div className="created">
                <span>{formatInTimeZone(new Date(item?.date), 'UTC', 'do MMM, yyy')}</span>
                <p>{formatInTimeZone(new Date(item?.date), 'UTC', 'h:mm aa')}</p>
              </div>
            </div>
          );
        })}
      </TableBody>

      {/* ===== Pagination ====== */}
      <PaginationView>
        <TableRowsSelect onChange={handleRowsPerPageChange} />
        <div className="paginate">
          <button className="prev" onClick={fetchPrevPage}>
            <PrevIcon />
          </button>
          <span>
            Page {page} of {Math.ceil(history?.data?.data?.meta?.totalItems / limit)} 
            {/* Page {page} of {Math.ceil(history?.data?.data?.meta?.totalItems / PAGE_LIMIT)}  */}
          </span>
          <button className="next" onClick={fetchNextPage}>
            <NextIcon />
          </button>
        </div>
      </PaginationView>
    </LenderHistoryView>
  )
}

const LenderHistoryView = styled.div`
  width: 100%;
  height: 100%;
  margin-top: 20px;
  padding-bottom: 30px;
`;

const TableHead = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};

  div {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    cursor: pointer;
    height: 100%;

    span {
      font-size: 0.8rem;
      font-weight: 500;
      color: ${({ theme }) => theme.colors?.black};
      /* text-transform: uppercase; */
    }

    .icon {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      margin-left: 4px;
      width: 10px;
    }
  }

  .ascending {
    color: ${({ theme }) => theme.colors.black};

    .up path {
      stroke: ${({ theme }) => theme.colors.black};
      color: ${({ theme }) => theme.colors.black};
    }
  }

  .descending {
    color: ${({ theme }) => theme.colors.black};

    .down path {
      stroke: ${({ theme }) => theme.colors.black};
      color: ${({ theme }) => theme.colors.black};
    }
  }

  .channel {
    flex: 0.8;
  }

  .event {
    flex: 0.3;
  }

  .provider {
    flex: 0.5;
  }

  .amount {
    flex: 0.5;
  }

  .type {
    flex: 1;
  }

  .status {
    flex: 0.5;
  }

  .created {
    flex: 0.5;
  }

  .updated {
    flex: 0.5;
  }
`;

const TableBody = styled.div`
  width: 100%;

  .item {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    border-bottom: 1px solid ${({ theme }) => theme.colors?.line};

    div {
      flex: 1;
      flex-wrap: wrap;

      span {
        font-size: 0.75rem;
        font-weight: 500;
        color: ${({ theme }) => theme.colors?.secondary};
        text-transform: capitalize;
      }

      p {
        font-size: 0.75rem;
        font-weight: 500;
        color: ${({ theme }) => theme.colors?.secondary};
        text-transform: capitalize;
      }
    }

    .channel {
      flex: 0.8;
    }

    .event {
      flex: 0.3;
    }

    .provider {
      flex: 0.5;
    }

    .amount {
      flex: 0.5;
    }

    /* .type {
      flex: 1;
    } */

    .status {
      flex: 0.5;
    }

    .created {
      flex: 0.5;
    }

    .updated {
      flex: 0.5;
    }

    .actions {
      /* flex: 0.5; */
      display: flex;
      align-items: center;
      justify-content: flex-end;
      position: relative;

      svg {
        cursor: pointer;

        path {
          stroke: ${({ theme }) => theme.colors?.secondary};
        }
      }

      .menu-actions {
        position: absolute;
        top: 22px;
        right: 0px;
        background-color: ${({ theme }) => theme.colors.white};
        box-shadow:
          0px 0px 2px rgba(125, 125, 125, 0.1),
          0px 2px 10px rgba(0, 0, 0, 0.1);
        border-radius: 10px;
        z-index: 10;
        width: 180px;
        border: none;

        button {
          width: 100%;
          padding: 16px 12px;
          font-size: 0.875rem;
          background-color: transparent;
          transition: all 0.2s ease-in-out;
          border: none;
          outline: none;
          display: flex;
          justify-content: flex-start;
          align-items: center;

          &:hover {
            background-color: ${({ theme }) => theme.colors.layer3};
          }

          span {
            color: ${({ theme }) => theme.colors.activeTitle};
            width: calc(100% - 35px);
            display: flex;
            justify-content: flex-start;
            text-align: left;
            font-weight: 400;
          }
        }
      }
    }
  }
`;

const PaginationView = styled.div`
  width: 100%;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .total {
    span {
      font-size: 0.875rem;
      font-weight: 500;
      color: ${({ theme }) => theme.colors?.secondary};
    }
  }

  .paginate {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 20px;
    padding: 2rem 0;

    button {
      width: 40px;
      height: 40px;
      border: none;
      outline: none;
      background-color: ${({ theme }) => theme.colors?.layer3};
      color: ${({ theme }) => theme.colors?.secondary};
      font-size: 0.875rem;
      font-weight: 500;
      border-radius: 6px;

      &:hover {
        background-color: ${({ theme }) => theme.colors?.layer2};
      }
    }

    span {
      margin: 0 10px;
      font-size: 0.875rem;
      font-weight: 500;
      color: ${({ theme }) => theme.colors?.secondary};
    }
  }
`;